import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useTutorCta } from "@/components/TutorCtaLink";
import {
  Smartphone, Download, ShieldCheck, Users, GraduationCap, BookOpen,
  Calendar, Bell, MessagesSquare, LineChart, ClipboardCheck, FileCheck,
  Globe, Headset, Sparkles, ArrowRight,
} from "lucide-react";

const BMT_LOGO = "/bookmytutor-logo.png";

const HIGHLIGHTS = [
  { Icon: Users,           label: "Parent Dashboard" },
  { Icon: GraduationCap,   label: "Tutor Dashboard" },
  { Icon: LineChart,       label: "Student Progress Tracking" },
  { Icon: Sparkles,        label: "Demo Class Management" },
  { Icon: ClipboardCheck,  label: "Attendance Monitoring" },
  { Icon: FileCheck,       label: "Assignment Management" },
  { Icon: Calendar,        label: "Class Scheduling" },
  { Icon: Bell,            label: "Notifications & Alerts" },
  { Icon: MessagesSquare,  label: "Secure Communication" },
  { Icon: Headset,         label: "Support & Helpdesk" },
  { Icon: BookOpen,        label: "Home Tuition Management" },
  { Icon: Smartphone,      label: "Online Tuition Management" },
  { Icon: Globe,           label: "International Student Management" },
];

/**
 * Book My Tutor promo section.
 *
 * Props:
 *  - variant: "full" (default — full promo with cards) | "strip" (compact above-footer band)
 *  - force: true to always render regardless of the show_bookmytutor_promo toggle (used by /book-my-tutor landing page)
 */
function PrimaryCta({ url, isExternal, onClick, className, testId, children }) {
  if (isExternal) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className} data-testid={testId}>
        {children}
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>
      {children}
    </a>
  );
}

export default function BookMyTutorPromo({ variant = "full", force = false }) {
  const settings = useSiteSettings();
  const { isExternal, onClick } = useTutorCta(`bmt-promo-${variant}`);
  const url = (settings?.external_tutor_url || "").trim() || "https://www.bookmytutor.com/download";
  const isOn = !!settings?.show_bookmytutor_promo;
  if (!force && !isOn) return null;

  if (variant === "strip") {
    return (
      <section className="bg-gradient-to-r from-brand-deep via-brand-royal to-brand-deep text-white relative overflow-hidden" data-testid="bmt-promo-strip">
        <div className="absolute inset-0 bg-dotted opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
          {/* Logo + copy block — mobile-first stacked, no overflow */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 min-w-0 text-center sm:text-left">
            <img
              src={BMT_LOGO}
              alt="Book My Tutor"
              className="h-10 sm:h-12 w-auto object-contain mx-auto sm:mx-0 flex-shrink-0"
              style={{ filter: "brightness(0) invert(1)" }}
              loading="lazy"
            />
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm sm:text-base lg:text-lg leading-snug break-words">
                Manage everything in the <span className="text-brand-cyan font-semibold">Book My Tutor</span> app
              </div>
              <div className="text-[11px] sm:text-xs text-white/70 mt-1 break-words">Official integrated ecosystem of Unique Home Tutors.</div>
            </div>
          </div>
          {/* CTA block — full-width on mobile, side-by-side from xs (380px+) */}
          <div className="flex flex-col xs:flex-row gap-2 w-full md:w-auto md:flex-shrink-0">
            <PrimaryCta
              url={url}
              isExternal={isExternal}
              onClick={onClick}
              testId="bmt-promo-strip-download"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-cyan text-brand-deep hover:bg-white px-4 sm:px-5 h-10 text-xs sm:text-sm font-medium transition w-full xs:w-auto"
            >
              <Download size={14} /> Download App
            </PrimaryCta>
            <Link
              to="/book-my-tutor"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/20 hover:bg-white/10 px-4 sm:px-5 h-10 text-xs sm:text-sm font-medium transition w-full xs:w-auto"
              data-testid="bmt-promo-strip-learn"
            >
              Learn more <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Full variant
  return (
    <section className="relative py-12 sm:py-16 lg:py-20 bg-slate-50 overflow-hidden" data-testid="bmt-promo-full">
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand-cyan/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-brand-royal/20 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto">
          <img src={BMT_LOGO} alt="Book My Tutor" className="mx-auto mb-5 sm:mb-7 h-12 sm:h-16 lg:h-20 w-auto object-contain" data-testid="bmt-promo-logo" />
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-royal/10 text-brand-royal text-[10px] sm:text-xs font-semibold uppercase tracking-widest">
            <ShieldCheck size={12} /> Official Technology Ecosystem
          </span>
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl mt-4 text-slate-900 leading-tight break-words">
            One app for your <span className="text-gradient-cyan">entire learning journey.</span>
          </h2>
          <p className="mt-4 sm:mt-5 text-slate-600 text-sm sm:text-base lg:text-lg leading-relaxed break-words">
            <strong>Book My Tutor</strong> is the official class-management platform and integrated ecosystem of Unique Home Tutors —
            designed to simplify the learning experience for parents, students, tutors and administrators.
            Manage class schedules, attendance, tutor assignments, demos, communication, progress tracking, notifications and academic updates from a single unified platform.
          </p>
        </div>

        {/* Highlights grid */}
        <div className="mt-8 sm:mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3" data-testid="bmt-promo-highlights">
          {HIGHLIGHTS.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 bg-white rounded-xl border border-slate-200/80 px-3 sm:px-4 py-2.5 sm:py-3 hover:shadow-sm transition min-w-0">
              <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-brand-royal/10 text-brand-royal grid place-items-center flex-shrink-0">
                <Icon size={14} />
              </span>
              <span className="text-xs sm:text-sm font-medium text-slate-800 truncate">{label}</span>
            </div>
          ))}
        </div>

        {/* Trust message */}
        <div className="mt-8 sm:mt-12 max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200/80 p-5 sm:p-6 lg:p-8 text-center">
          <div className="text-brand-cyan text-[10px] sm:text-xs font-semibold uppercase tracking-widest">Trusted Daily</div>
          <p className="mt-2 text-slate-700 text-sm sm:text-base lg:text-lg leading-relaxed break-words">
            Thousands of parents, students and tutors rely on the integrated Unique Home Tutors ecosystem to manage their educational journey
            <span className="text-brand-royal font-semibold"> efficiently, securely and transparently.</span>
          </p>
        </div>

        {/* CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <h3 className="font-display text-xl sm:text-2xl lg:text-3xl text-brand-royal break-words">Download the Official Book My Tutor App</h3>
          <p className="mt-2 text-xs sm:text-sm text-slate-600 max-w-2xl mx-auto break-words">
            Manage your entire learning experience through the official integrated ecosystem of Unique Home Tutors.
          </p>
          <div className="mt-6 flex flex-col xs:flex-row flex-wrap items-stretch xs:items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full bg-brand-royal hover:bg-brand-royal/90 text-white h-12 px-6 sm:px-8 w-full xs:w-auto justify-center">
              <PrimaryCta url={url} isExternal={isExternal} onClick={onClick} testId="bmt-promo-full-download">
                <Download size={16} className="mr-1.5" /> Download App
              </PrimaryCta>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 sm:px-8 w-full xs:w-auto justify-center">
              <PrimaryCta url={url} isExternal={isExternal} onClick={onClick} testId="bmt-promo-full-getstarted">
                Get Started Today <ArrowRight size={16} className="ml-1.5" />
              </PrimaryCta>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
